import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './event-detail-page.css';

const EventDetailPage = () => {
    const { id } = useParams();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showPrevious, setShowPrevious] = useState(false);
    const [eventData, setEventData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        document.title = "Marlo - Event Detail";
        
        let cancelled = false;
        
        const loadEventDetail = async () => {
            if (cancelled) return;
            
            setLoading(true);
            setError(null);
            
            try {
                console.log('Loading event detail for ID:', id);
                // 调用HTML端点的活动详情API（无需认证）
                const response = await axios.get(`/api/v1/html/${id}`);
                
                if (cancelled) return; // 如果组件已卸载，不更新状态
                
                console.log('Event detail API response:', response.data);
                
                if (response.data && response.data.succeed) {
                    setEventData(response.data.data);
                    console.log('Event detail data loaded:', response.data.data);
                } else {
                    setError(response.data?.message || 'Failed to load event details');
                    console.error('API error:', response.data?.message);
                }
            } catch (err) {
                if (cancelled) return;
                console.error('Failed to load event details:', err);
                setError('Failed to load event details');
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadEventDetail();
        
        return () => {
            cancelled = true;
        };
    }, [id]);

    // 模拟缩略图数据 (7个图片，超过6个显示next按钮)
    const thumbnails = Array.from({length: 7}, (_, index) => ({
        id: index + 1,
        src: require('../images/event/card.png')
    }));

    // 模拟头像数据
    const participants = [
        { id: 1, name: 'Peal Jiang', avatar: require('../images/event/card.png') },
        { id: 2, name: 'Annie Liu', avatar: require('../images/event/card.png') },
        { id: 3, name: 'User 3', avatar: require('../images/event/card.png') },
        { id: 4, name: 'User 4', avatar: require('../images/event/card.png') },
        { id: 5, name: 'User 5', avatar: require('../images/event/card.png') },
    ];

    const handleThumbnailClick = (index) => {
        setCurrentImageIndex(index);
    };

    const handleThumbnailNavigation = () => {
        setShowPrevious(!showPrevious);
    };

    // 生成地图图片URL - 根据iOS逻辑
    const getMapImageUrl = () => {
        if (!eventData || !eventData.Coverages || eventData.Coverages.length === 0) {
            return '';
        }
        
        const coverage = eventData.Coverages[0];
        return `/api/v1/wave/${eventData.ID}/map`;
    };

    // 打开地图应用 - 根据iOS逻辑
    const handleOpenInMaps = () => {
        if (!eventData || !eventData.Coverages || eventData.Coverages.length === 0) {
            return;
        }
        
        const coverage = eventData.Coverages[0];
        const latitude = coverage.Latitude;
        const longitude = coverage.Longitude;
        const locationName = eventData.DetailedLocation || eventData.Title || 'Location';
        const encodedLocationName = encodeURIComponent(locationName);
        const urlString = `http://maps.apple.com/?ll=${latitude},${longitude}&z=15&q=${encodedLocationName}`;
        
        window.open(urlString, '_blank');
    };

    // 显示的缩略图 (最多6个 + 导航按钮)
    const displayThumbnails = showPrevious ? 
        thumbnails.slice(Math.max(0, thumbnails.length - 6), thumbnails.length) :
        thumbnails.slice(0, 6);
    const hasMoreThumbnails = thumbnails.length > 6;

    // 根据iOS逻辑渲染参与者头像
    const renderParticipantAvatars = () => {
        if (!eventData || !eventData.Behaviors) return null;

        // 筛选参与者：AttendeeStatus == 2 且不是创建人
        const attendees = eventData.Behaviors.filter(behavior => 
            behavior.AttendeeStatus === 2 && behavior.UserID !== eventData.UserID
        );
        
        if (attendees.length === 0 && eventData.IsExplore) {
            // Explore页面无参与者时显示默认头像
            return (
                <div className="avatar-item">
                    <img src={require('../images/avatar_default.png')} alt="No participants" />
                </div>
            );
        }

        const elements = [];
        const limit = Math.min(attendees.length, 4); // 最多显示4个

        // Explore页面不显示创建人头像，只显示参与者
        for (let i = 0; i < limit; i++) {
            const attendee = attendees[i];
            const user = attendee.User;
            elements.push(
                <div key={`attendee-${attendee.ID}`} className="avatar-item">
                    <img 
                        src={user?.ProfilePicture || require('../images/avatar_default.png')} 
                        alt={user?.FirstName || 'Participant'} 
                        onError={(e) => {
                            e.target.src = require('../images/avatar_default.png');
                        }}
                    />
                </div>
            );
        }

        // 如果超过4人，显示"+N"
        if (attendees.length > 4) {
            elements.push(
                <div key="more" className="avatar-item avatar-more">
                    {attendees.length - 4}+
                </div>
            );
        }

        return elements;
    };

    // 生成参与者文字描述
    const getParticipantsText = () => {
        if (!eventData || !eventData.Behaviors) return 'No participants yet';

        const attendees = eventData.Behaviors.filter(behavior => 
            behavior.AttendeeStatus === 2 && behavior.UserID !== eventData.UserID
        );

        if (attendees.length === 0) {
            return 'No participants yet';
        }

        // 获取前两个参与者的名字
        const firstNames = attendees.slice(0, 2).map(attendee => {
            const user = attendee.User;
            return user?.FirstName || 'Anonymous';
        });

        if (attendees.length === 1) {
            return firstNames[0];
        } else if (attendees.length === 2) {
            return `${firstNames[0]} and ${firstNames[1]}`;
        } else if (attendees.length > 2) {
            return `${firstNames[0]}, ${firstNames[1]} and ${attendees.length - 2} other${attendees.length - 2 > 1 ? 's' : ''}`;
        }

        return 'No participants yet';
    };

    // 处理应用内跳转逻辑 - 触发Header的下载弹窗
    const handleOpenInApp = () => {
        // 触发全局事件，打开Header的下载弹窗
        window.dispatchEvent(new Event('openDownloadModal'));
    };

    // 骨架屏组件 - 手机端顺序：标题→图片→时间→地点→Who's going→I'm In→Event Details
    const SkeletonLoader = () => (
        <div className="skeleton-container">
            <div className="skeleton-columns">
                {/* 左列 - PC端显示 */}
                <div className="skeleton-left">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-participants">
                        <div className="skeleton-text skeleton-title-sm"></div>
                        <div className="skeleton-avatars">
                            <div className="skeleton-avatar"></div>
                            <div className="skeleton-avatar"></div>
                            <div className="skeleton-avatar"></div>
                        </div>
                    </div>
                    <div className="skeleton-button"></div>
                </div>
                {/* 右列 - PC端显示 */}
                <div className="skeleton-right">
                    <div className="skeleton-text skeleton-title-lg"></div>
                    <div className="skeleton-info-row">
                        <div className="skeleton-icon"></div>
                        <div className="skeleton-info-text">
                            <div className="skeleton-text skeleton-line"></div>
                            <div className="skeleton-text skeleton-line-short"></div>
                        </div>
                    </div>
                    <div className="skeleton-info-row">
                        <div className="skeleton-icon"></div>
                        <div className="skeleton-info-text">
                            <div className="skeleton-text skeleton-line"></div>
                        </div>
                    </div>
                    <div className="skeleton-detail-module">
                        <div className="skeleton-text skeleton-title-sm"></div>
                        <div className="skeleton-text skeleton-line"></div>
                        <div className="skeleton-text skeleton-line"></div>
                        <div className="skeleton-text skeleton-line-short"></div>
                    </div>
                </div>
            </div>
            {/* 手机端单列布局 */}
            <div className="skeleton-mobile">
                <div className="skeleton-text skeleton-title-lg"></div>
                <div className="skeleton-image"></div>
                <div className="skeleton-info-row">
                    <div className="skeleton-icon"></div>
                    <div className="skeleton-info-text">
                        <div className="skeleton-text skeleton-line"></div>
                        <div className="skeleton-text skeleton-line-short"></div>
                    </div>
                </div>
                <div className="skeleton-info-row">
                    <div className="skeleton-icon"></div>
                    <div className="skeleton-info-text">
                        <div className="skeleton-text skeleton-line"></div>
                    </div>
                </div>
                <div className="skeleton-participants">
                    <div className="skeleton-text skeleton-title-sm"></div>
                    <div className="skeleton-avatars">
                        <div className="skeleton-avatar"></div>
                        <div className="skeleton-avatar"></div>
                        <div className="skeleton-avatar"></div>
                    </div>
                </div>
                <div className="skeleton-button"></div>
                <div className="skeleton-detail-module">
                    <div className="skeleton-text skeleton-title-sm"></div>
                    <div className="skeleton-text skeleton-line"></div>
                    <div className="skeleton-text skeleton-line"></div>
                    <div className="skeleton-text skeleton-line-short"></div>
                </div>
            </div>
        </div>
    );

    // 加载中显示骨架屏
    if (loading) {
        return (
            <div className="event-detail-page">
                <Header />
                <main className="event-detail-main">
                    <div className="event-detail-content">
                        <SkeletonLoader />
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // 错误状态
    if (error || !eventData) {
        return (
            <div className="event-detail-page">
                <Header />
                <main className="event-detail-main">
                    <div className="event-detail-content">
                        <div className="error-message">
                            {error || 'Event not found'}
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="event-detail-page">
            <Header />
            
            <main className="event-detail-main">
                <div className="event-detail-content">
                    {/* 左右两列布局 */}
                    <div className="event-detail-columns">
                        {/* 左列 */}
                        <div className="event-detail-left">
                            {/* 主图片 - 根据iOS逻辑：如果有Media则显示，否则使用WaveTypeImage */}
                            <div className="main-image">
                                {eventData.Media && eventData.Media.length > 0 ? (
                                    <img 
                                        src={eventData.Media[currentImageIndex]?.FilePath || eventData.Media[0].FilePath} 
                                        alt={eventData.Title || ''} 
                                        onError={(e) => {
                                            if (eventData.WaveTypeImage) {
                                                e.target.src = eventData.WaveTypeImage;
                                            } else {
                                                e.target.style.display = 'none';
                                            }
                                        }}
                                    />
                                ) : eventData.WaveTypeImage ? (
                                    <img 
                                        src={eventData.WaveTypeImage} 
                                        alt={eventData.Title || ''} 
                                    />
                                ) : null}
                            </div>

                            {/* 缩略图行 - 根据iOS逻辑：只有多张图片时才显示 */}
                            {eventData && eventData.Media && eventData.Media.length > 1 && (
                                <div className="thumbnails-row">
                                    {eventData.Media.slice(0, 6).map((media, index) => (
                                        <div 
                                            key={media.ID} 
                                            className={`thumbnail ${index !== currentImageIndex ? 'active' : ''}`}
                                            onClick={() => handleThumbnailClick(index)}
                                        >
                                            <img 
                                                src={media.ThumbnailFilePath || media.FilePath} 
                                                alt={`Thumbnail ${index + 1}`} 
                                            />
                                        </div>
                                    ))}
                                    
                                    {eventData.Media.length > 6 && (
                                        <div className="thumbnail-nav next">
                                            <img src={require('../images/event/next.png')} alt="More" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 参与者模块 - 根据iOS逻辑渲染 */}
                            <div className="participants-module">
                                <div className="participants-title">Who's going</div>
                                <div className="participants-divider"></div>
                                
                                {/* 头像组 - 根据iOS WaveCapacity逻辑 */}
                                <div className="avatars-group">
                                    {renderParticipantAvatars()}
                                </div>
                                
                                {/* 参与者文字 - 根据真实数据 */}
                                <div className="participants-text">
                                    {getParticipantsText()}
                                </div>
                                
                                {/* 分隔线 */}
                                <div className="participants-bottom-divider"></div>
                            </div>

                            {/* Join Chatroom 按钮 */}
                            <button className="im-in-button" onClick={handleOpenInApp}>
                                Join Chatroom
                            </button>
                        </div>

                        {/* 右列 */}
                        <div className="event-detail-right">
                            <h1 className="event-title">
                                {/* 根据iOS逻辑：使用eventData.Title */}
                                {eventData.Title || ''}
                            </h1>

                            {/* 时间和地点信息 */}
                            <div className="event-info-section">
                                {/* 时间信息行 */}
                                <div className="info-row">
                                    <div className="info-icon">
                                        <img src={require('../images/event/time_color.png')} alt="Time" />
                                    </div>
                                    <div className="info-text">
                                        <div className="info-text-primary">
                                            {/* 根据iOS逻辑：使用EventDateStr和EventDayOfWeek */}
                                            {eventData.EventDateStr && eventData.EventDayOfWeek 
                                                ? `${eventData.EventDayOfWeek}, ${eventData.EventDateStr}`
                                                : ''}
                                        </div>
                                        <div className="info-text-secondary">
                                            {/* 根据iOS逻辑：使用EventTimeStr */}
                                            {eventData.EventTimeStr || ''}
                                        </div>
                                    </div>
                                </div>

                                {/* 地点信息行 */}
                                <div className="info-row">
                                    <div className="info-icon">
                                        <img src={require('../images/event/addr_color.png')} alt="Location" />
                                    </div>
                                    <div className="info-text single-line">
                                        <div className="info-text-primary">
                                            {/* 根据iOS逻辑：使用DetailedLocation */}
                                            {eventData.DetailedLocation || ''}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Event Details 模块 */}
                            <div className="detail-module">
                                <div className="detail-title">Event Details</div>
                                <div className="detail-divider"></div>
                                <div className="detail-content">
                                    {/* 根据iOS逻辑：使用Body字段 */}
                                    {eventData.Body ? (
                                        <div style={{ whiteSpace: 'pre-wrap' }}>
                                            {eventData.Body}
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {/* Location 模块 - 只在explore页面显示 */}
                            {eventData?.IsExplore && (
                                <div className="location-module">
                                    <div className="detail-title">Location</div>
                                    <div className="detail-divider"></div>
                                    <div className="detail-content">
                                        {eventData.DetailedLocation || ''}
                                    </div>
                                </div>
                            )}

                            {/* 地图信息 - 根据iOS逻辑：使用Coverages数据 */}
                            {eventData && eventData.Coverages && eventData.Coverages.length > 0 && (
                                <div className="map-placeholder" onClick={() => handleOpenInMaps()}>
                                    <img
                                        src={getMapImageUrl()}
                                        alt="Location Map"
                                        className="location-map-image"
                                        onError={(e) => {
                                            console.error("地图加载失败");
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                    <img
                                        src={require('../images/green_point.png')}
                                        alt="Location Marker"
                                        className="map-marker-point"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default EventDetailPage;
